/**
 * CardRoiCard — Admin Dashboard widget showing which digital-business-card
 * hosts (Linktree, Blinq, Popl, Bento, Milkshake, etc.) actually paid off
 * during enrichment. Each aggregator hit costs ~1 Firecrawl credit and
 * typically returns 3-6 social URLs vs. 5-30 credits doing per-platform
 * site: searches, so hosts with a high "fields per credit" ratio are the
 * best ROI targets. Admins use this to prioritize which apps to build
 * outreach templates around.
 *
 * Backend: GET /api/admin/card-roi-stats — aggregates every enrichment_
 * event's `digital_card_hits[]` into per-host rows sorted by fields_found
 * DESC.
 */
import React, { useEffect, useState } from "react";
import { CreditCard, ExternalLink, Loader2, TrendingUp } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function CardRoiCard() {
  const [state, setState] = useState({ loading: true, data: null, err: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/card-roi-stats");
        if (!cancelled) setState({ loading: false, data, err: "" });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, err: formatApiError(e) });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="card-roi-widget"
    >
      <header className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <CreditCard className="h-4 w-4 text-indigo-500" />
        <h2 className="text-base md:text-lg font-semibold text-slate-900">
          Digital business card ROI
        </h2>
        <span className="text-xs text-slate-500">
          which aggregator apps actually pay off during enrichment
        </span>
      </header>
      <div className="px-4 py-4">
        {state.loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="card-roi-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading card-hit data…
          </div>
        ) : state.err ? (
          <div className="text-sm text-rose-600" data-testid="card-roi-error">
            Couldn&apos;t load stats: {state.err}
          </div>
        ) : (
          <Body data={state.data} />
        )}
      </div>
    </section>
  );
}

function Body({ data }) {
  const totals = data?.totals || {};
  const byHost = data?.by_host || [];
  const recent = data?.recent_hits || [];

  if (!totals.hits) {
    return (
      <div className="text-sm text-slate-500 space-y-2" data-testid="card-roi-empty">
        <p>
          <strong className="text-slate-700">No aggregator hits yet.</strong>{" "}
          As enrichment finds realtors with a Linktree, Blinq, Popl, Milkshake or
          similar profile page linked from their brokerage or personal site,
          this widget will show which hosts return the most socials per credit
          spent — so you can double down on the apps your local market actually
          uses.
        </p>
        <p className="text-xs">
          Nothing to configure — the data starts flowing on the next enrichment run.
        </p>
      </div>
    );
  }

  const maxFields = Math.max(...byHost.map((r) => r.fields_found), 1);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label="Card lookups"
          value={totals.hits.toLocaleString()}
          hint={
            totals.errors
              ? `${totals.successful_hits} ok · ${totals.errors} failed`
              : "all clean hits"
          }
          testid="card-roi-total-hits"
        />
        <Stat
          label="Credits spent"
          value={totals.credits.toLocaleString()}
          hint={
            totals.fields_per_credit != null
              ? `${totals.fields_per_credit} fields per credit`
              : "—"
          }
          testid="card-roi-credits"
        />
        <Stat
          label="Social fields harvested"
          value={totals.fields_found.toLocaleString()}
          hint={
            totals.fields_per_hit != null
              ? `${totals.fields_per_hit} per successful hit`
              : "—"
          }
          testid="card-roi-fields"
        />
        <Stat
          label="Success rate"
          value={totals.success_rate != null ? `${totals.success_rate}%` : "—"}
          hint={`${totals.name_matched} name-matched`}
          testid="card-roi-success"
        />
      </div>

      {/* Per-host breakdown as a compact horizontal-bar row. Sorted by
          fields_found DESC (highest-ROI host first). */}
      <div className="rounded-md border border-slate-200 overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
          Top-performing hosts
        </div>
        <div className="divide-y divide-slate-100" data-testid="card-roi-host-list">
          {byHost.map((row) => {
            const barPct = Math.max(4, Math.round((row.fields_found / maxFields) * 100));
            return (
              <div
                key={row.host}
                className="px-3 py-2.5 flex items-center gap-3 text-sm"
                data-testid={`card-roi-host-${row.host.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="w-28 shrink-0 truncate font-medium text-slate-900" title={row.host}>
                  {row.host}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="h-2 bg-slate-100 rounded overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-indigo-500"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3">
                    <span>
                      <strong className="text-slate-800">{row.fields_found}</strong> fields
                    </span>
                    <span>
                      {row.hits} hit{row.hits === 1 ? "" : "s"}
                      {row.errors ? ` (${row.errors} failed)` : ""}
                    </span>
                    <span>{row.credits} credit{row.credits === 1 ? "" : "s"}</span>
                    {row.fields_per_credit != null ? (
                      <span className="text-emerald-700">
                        {row.fields_per_credit} fields/credit
                      </span>
                    ) : null}
                    {row.name_matched ? (
                      <span className="text-indigo-700">{row.name_matched} name-matched</span>
                    ) : null}
                  </div>
                </div>
                {row.example_url ? (
                  <a
                    href={row.example_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="shrink-0 text-slate-400 hover:text-indigo-600 transition-colors"
                    title={`Open an example ${row.host} page`}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {recent.length ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-600 hover:text-slate-900 select-none">
            Show last {recent.length} card lookups
          </summary>
          <ul className="mt-2 space-y-1.5 text-xs">
            {recent.map((r, i) => (
              <li
                key={`${r.url}-${i}`}
                className="flex items-center gap-2 text-slate-600"
                data-testid={`card-roi-recent-${i}`}
              >
                <span className="w-16 shrink-0 font-medium text-slate-900 truncate">{r.host}</span>
                <span className="truncate flex-1">
                  {r.realtor_name}
                  {r.error ? (
                    <span className="text-rose-600 ml-1">· {r.error}</span>
                  ) : (
                    <span className="text-emerald-700 ml-1">
                      · {r.fields_found} field{r.fields_found === 1 ? "" : "s"}
                    </span>
                  )}
                </span>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="shrink-0 text-slate-400 hover:text-indigo-600"
                  title={r.url}
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function Stat({ label, value, hint, testid }) {
  return (
    <div
      className="rounded-md border border-slate-200 px-3 py-2.5 bg-slate-50/40"
      data-testid={testid}
    >
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900 tabular-nums mt-0.5">{value}</div>
      {hint ? <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div> : null}
    </div>
  );
}
